import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint.tsx";
import { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton.tsx";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { syncQuotaConsumers } from "~/modules/quota/api/quota/quotaApiDispatchers.ts";
import type { CreateQuotaConsumerData, QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import QuotaConsumerSelector from "~/modules/quota/components/CreateQuotaSidebar/components/QuotaConsumerSelector.tsx";
import type { QuotaConsumerOrderInfo } from "~/modules/quota/components/formSections/QuotaConsumersFormSection.tsx";
import QuotaConsumersFormSection from "~/modules/quota/components/formSections/QuotaConsumersFormSection.tsx";
import { useQuotaConsumerOrderInfo } from "~/modules/quota/hooks/useQuotaConsumerOrderInfo.ts";
import {
	getUpdateQuotaConsumersSchema,
	prepareSyncQuotaConsumersPayload,
} from "~/modules/quota/utils/quotaFormUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

// One consumer in the form. `consumerId` is the stored consumer id, or null for a new consumer.
// `budgetCentsConsumed` is only for the validation. A new consumer has 0.
type UpdateQuotaConsumerFormEntry = CreateQuotaConsumerData & {
	budgetCentsConsumed: number;
	consumerId: string | null;
};

export type UpdateQuotaConsumersFormData = {
	amountCents: number;
	consumers: UpdateQuotaConsumerFormEntry[];
};

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations;
};

export const UpdateQuotaConsumersForm = ({ onCancel, onSuccess, quota }: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [showOrderSelect, setShowOrderSelect] = useState(false);

	const availableOrderInfoById = useQuotaConsumerOrderInfo(quota.companyId);

	// The available consumers do not list the orders that this quota holds already.
	// The labels of those orders come from the order embed of each consumer.
	const orderInfoById = useMemo(() => {
		const map: Record<string, QuotaConsumerOrderInfo> = { ...availableOrderInfoById };
		quota.consumers.forEach((consumer) => {
			if (consumer.order) {
				map[consumer.orderId] = {
					orderTitle: consumer.order.title,
					phaseTitle: consumer.order.projectPhase.title,
					projectTitle: consumer.order.project.titleWithNumber,
					projectId: consumer.order.projectId,
				};
			}
		});
		return map;
	}, [availableOrderInfoById, quota.consumers]);

	const consumedCentsByOrderId = useMemo(() => Object.fromEntries(quota.consumers
		.map((consumer) => [consumer.orderId, consumer.budgetCentsConsumed])), [quota.consumers]);

	const schema = useMemo(() => getUpdateQuotaConsumersSchema(), []);

	const defaultValues = useMemo(() => {
		return {
			// Only a shell has no amount. The view does not open this sidebar on a shell.
			amountCents: quota.amountCents ?? 0,
			consumers: quota.consumers.map((consumer) => ({
				budgetCentsConsumed: consumer.budgetCentsConsumed,
				consumerId: consumer.id,
				orderId: consumer.orderId,
				topsUpBudget: consumer.topsUpBudget,
				budgetCentsAssigned: consumer.budgetCentsAssigned,
				timeTrackingEnabled: consumer.timeTrackingEnabled,
			})),
		};
	}, [quota.amountCents, quota.consumers]);

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isSubmitted, isValid },
		trigger,
	} = useForm<UpdateQuotaConsumersFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<UpdateQuotaConsumersFormData>(schema),
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "consumers",
	});

	// The budget check is a cross-field rule. Its error sits on `amountCents`, which this form never
	// changes, so a change to a consumer does not clear it on its own. After the first submit,
	// re-validate the amount and each consumer budget when a consumer changes, as the create forms do.
	const watchedConsumers = useWatch({ control, name: "consumers" });

	useEffect(() => {
		if (!isSubmitted) {
			return;
		}
		const consumerBudgetFields = (watchedConsumers ?? []).map(
			(_, index) => `consumers.${index}.budgetCentsAssigned` as const,
		);
		void trigger(["amountCents", ...consumerBudgetFields]);
	}, [watchedConsumers, isSubmitted, trigger]);

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const handleConfirmOrders = useCallback((consumers: CreateQuotaConsumerData[]) => {
		consumers.forEach((consumer) => append({ ...consumer, budgetCentsConsumed: 0, consumerId: null }));
		setShowOrderSelect(false);
	}, [append]);

	const onSubmit = useCallback(async (data: UpdateQuotaConsumersFormData) => {
		try {
			setIsBusy(true);
			await syncQuotaConsumers(quota.companyId, quota.id, prepareSyncQuotaConsumersPayload(data));
			onSuccess();
		} catch (error) {
			// The backend gives the definite answer, for example on a consumer with consumed budget.
			const apiError = handleApiError(error);
			if (apiError.type === ApiResponseErrorType.VALIDATION) {
				setServerErrorMessage(Object.values(apiError.messages)
					.flat()
					.map((detail) => detail.message)
					.join("\n"));
			} else {
				setServerErrorMessage(t("form:unexpectedError"));
			}
			setIsBusy(false);
		}
	}, [onSuccess, quota.companyId, quota.id]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			{showOrderSelect && <QuotaConsumerSelector companyId={quota.companyId}
													   excludedOrderIds={fields.map((field) => field.orderId)}
													   onCancel={() => setShowOrderSelect(false)}
													   onConfirm={handleConfirmOrders} />}
			{!showOrderSelect && <>
				<SidebarHeader>
					<SidebarHeaderHeadline>Bestellungen bearbeiten</SidebarHeaderHeadline>
				</SidebarHeader>
				<SidebarContent>
					{isBusy && <SidebarBusyOverlay />}
					{!!serverErrorMessage &&
						<SidebarErrorOverlay title="Update fehlgeschlagen">
							<span className="whitespace-pre-line">{serverErrorMessage}</span>
						</SidebarErrorOverlay>}
					<FormSectionGroup>
						<QuotaConsumersFormSection control={control}
												   fields={fields}
												   budgetErrorMessage={errors.amountCents?.message}
												   consumedCentsByOrderId={consumedCentsByOrderId}
												   onAddClick={() => setShowOrderSelect(true)}
												   onRemove={remove}
												   orderInfoById={orderInfoById} />
					</FormSectionGroup>
				</SidebarContent>
				<SidebarFooter>
					<FormHasErrorsHint show={isSubmitted && !isValid}
									   className="mr-2" />
					<SubmitButton busy={isBusy}
								  disabled={!formIsSubmittable}>
						{t("form:buttonTexts.update")}
					</SubmitButton>
					<Button theme="white"
							onClick={onCancel}>
						{t("form:buttonTexts.cancel")}
					</Button>
				</SidebarFooter>
			</>}
		</form>);
};
